// src/components/dashboard/AdminCancelledOrders.jsx

import React, { useEffect, useState } from "react";
import { Package, Search, ChevronLeft, ChevronRight, Book } from "lucide-react";
import OrderManager from "../../services/dashboard/OrderManager";
import { toast } from "react-hot-toast";
import { motion } from "framer-motion";

export default function AdminCancelledOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchCancelledOrders();
  }, []);

  const fetchCancelledOrders = async () => {
    try {
      setLoading(true);
      const data = await OrderManager.getCancelledOrders();
      setOrders(data || []);
    } catch (error) {
      console.error("Failed to fetch cancelled orders:", error);
      toast.error("Failed to load cancelled orders");
    } finally {
      setLoading(false);
    }
  };

  // Helper to safely format the creation/cancelled date from array or string
  const formatDate = (dateInput) => {
    if (!dateInput) return "N/A";
    let d;
    if (Array.isArray(dateInput)) {
      d = new Date(
        dateInput[0],
        dateInput[1] - 1,
        dateInput[2],
        dateInput[3] || 0,
        dateInput[4] || 0,
        dateInput[5] || 0
      );
    } else {
      d = new Date(dateInput);
    }
    
    // Check if valid date
    if (isNaN(d.getTime())) return "Invalid Date";
    
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get exact cancellation time from statusTimestamps if available
  const getCancellationDate = (order) => {
    if (order.statusTimestamps && order.statusTimestamps.CANCELLED) {
      return formatDate(order.statusTimestamps.CANCELLED);
    }
    // Fallback to order creation date or order date if no explicit cancellation timestamp
    return formatDate(order.creationDate || order.orderDate);
  };

  // Filter orders by search term
  const filteredOrders = orders.filter((order) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (order.book?.bookName || "").toLowerCase().includes(searchLower) ||
      (order.user?.firstName || "").toLowerCase().includes(searchLower) ||
      (order.user?.lastName || "").toLowerCase().includes(searchLower) ||
      (order.orderId || "").toString().includes(searchLower)
    );
  });

  // Sort orders descending (latest cancelled first)
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const getDate = (order) => {
      let d = order.statusTimestamps?.CANCELLED || order.creationDate || order.orderDate;
      if (Array.isArray(d)) {
        return new Date(d[0], d[1] - 1, d[2], d[3] || 0, d[4] || 0, d[5] || 0).getTime();
      }
      return new Date(d).getTime() || 0;
    };
    return getDate(b) - getDate(a);
  });

  // Pagination logic
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
     
            Cancelled Orders
          </h1>
       
        </div>
      </div>

      {/* Controls: Search */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="relative w-full sm:max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search by book, user, or order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white transition-shadow"
          />
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing {currentOrders.length} of {sortedOrders.length} orders
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Order Details
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User Info
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cancelled Date
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : currentOrders.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                      <Package className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" />
                      <p className="text-lg font-medium">No cancelled orders found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentOrders.map((order) => (
                  <motion.tr
                    key={order.orderId}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    {/* Order Details */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-16 bg-gray-100 dark:bg-gray-700 rounded-md overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-600">
                          {order.book?.bookImages?.[0]?.imagePath ? (
                            <img
                              src={`http://localhost:8080/bookImages/${order.book.bookImages[0].imagePath}`}
                              alt={order.book.bookName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = "/placeholder-book.png";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Book size={20} />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white line-clamp-1">
                            {order.book?.bookName || "Unknown Book"}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Order ID: #{order.orderId}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* User Info */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {order.user?.firstName} {order.user?.lastName}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {order.user?.emailId || "N/A"}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          ID: {order.user?.userId}
                        </span>
                      </div>
                    </td>

                    {/* Cancelled Date */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {getCancellationDate(order)}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800/80">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400" />
                        Cancelled
                      </span>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {!loading && sortedOrders.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-medium">{indexOfFirstOrder + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(indexOfLastOrder, sortedOrders.length)}
              </span>{" "}
              of <span className="font-medium">{sortedOrders.length}</span> entries
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
